import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'app/store';

// ── Types ─────────────────────────────────────────────────────────────────────

/** One CloudWatch log entry as returned by the backend */
export interface CloudWatchLogEntry {
  '@timestamp': string;
  '@message': {
    level: string;
    message: string;
    function: string;
    timestamp: string;
    logId: string;
    method: string;
    path: string;
    brandId: string | null;
    locationId: string | null;
    storeId?: string | null;
    orderId?: string | null;
    system?: string | null;
    requestTimestamp: string;
    status: number;
    responseTime: string;
    success: boolean;
    responseMessage: string;
    resMessage?: string;
    response?: any;
    ip: string;
    requestSource?: Record<string, any>;
    userAgent: string | null;
    userId: string | null;
    username: string;
    userName?: string;
    module: string;
    action: string;
    apiAction?: string;
    mainBrandId: string | null;
    ref_id: string | null;
    requestQuery: Record<string, any>;
    query?: Record<string, any>;
    requestBody: Record<string, any>;
    body?: Record<string, any>;
  };
  '@ptr': string;
}

export type ActivityLogTabType = 'admin' | 'backend';

export interface TabLogData {
  logs: CloudWatchLogEntry[];
  total: number;
  page: number;
  limit: number;
  nextCursor: string | null;
  hasMore: boolean;
  loading: boolean;
  isFetchingMore: boolean;
  module: string;
  moduleLabel: string;
}

export interface ActivityLogState {
  activeTab: ActivityLogTabType;
  modulesLoading: boolean;
  /** Available modules from GET /activityHistory/getModules */
  modules: { key: string; label: string; description: string }[];
  /** Isolated sub-states for each tab */
  admin: TabLogData;
  backend: TabLogData;
  /** Active tab mirror properties (backward compatible) */
  loading: boolean;
  isFetchingMore: boolean;
  logs: CloudWatchLogEntry[];
  total: number;
  page: number;
  limit: number;
  nextCursor: string | null;
  hasMore: boolean;
  module: string;
  moduleLabel: string;
  message: string;
  success: boolean;
}

export interface GetLogsPayload {
  tab?: ActivityLogTabType;
  module?: string;
  brandId?: string;
  locationId?: string;
  refId?: string;
  name?: string;
  action?: string;
  method?: string;
  level?: string;
  status?: number | string;
  userId?: string;
  adminUserId?: string;
  username?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  nextCursor?: string | null;
  isQuery?: boolean;
  query?: string;
}

export interface GetModulesSuccessPayload {
  data?: ActivityLogState['modules'];
  success?: boolean;
  message?: string;
  [key: string]: any;
}

export interface GetModulesFailedPayload {
  message?: string;
}

export interface GetActivityLogsSuccessPayload {
  res: any;
  tab?: ActivityLogTabType;
  isAppend?: boolean;
  requestedLimit?: number;
}

export interface GetActivityLogsFailedPayload {
  message?: string;
  tab?: ActivityLogTabType;
  isAppend?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export const normalizeLogEntry = (entry: Record<string, any>): CloudWatchLogEntry => {
  let message = entry?.['@message'] ?? entry?.message ?? {};
  if (typeof message === 'string') {
    try {
      message = JSON.parse(message);
    } catch {
      message = { message };
    }
  }

  // Extract response message across all variations: resMessage, response.message, response, responseMessage
  const extractedResponseMsg =
    message?.resMessage ||
    (typeof message?.response === 'string'
      ? message.response
      : message?.response?.message || message?.response?.msg) ||
    message?.responseMessage ||
    entry?.resMessage ||
    (typeof entry?.response === 'string'
      ? entry.response
      : entry?.response?.message || entry?.response?.msg) ||
    entry?.responseMessage ||
    '';

  const extractedUsername =
    message?.userName ||
    message?.username ||
    entry?.userName ||
    entry?.username ||
    '';

  const extractedAction =
    message?.apiAction ||
    message?.action ||
    entry?.apiAction ||
    entry?.action ||
    '';

  const extractedIp =
    message?.ip ||
    message?.requestSource?.ip ||
    entry?.ip ||
    entry?.requestSource?.ip ||
    '';

  const extractedQuery =
    message?.requestQuery ||
    message?.query ||
    entry?.requestQuery ||
    entry?.query ||
    {};

  const extractedBody =
    message?.requestBody ||
    message?.body ||
    entry?.requestBody ||
    entry?.body ||
    {};

  const mergedMessage = {
    level: String(message?.level || entry?.level || 'info').toLowerCase(),
    message: message?.message || entry?.message || '',
    function: message?.function || entry?.function || '',
    timestamp: message?.timestamp || entry?.timestamp || entry?.['@timestamp'] || '',
    logId: message?.logId || entry?.logId || entry?._id || '',
    method: String(message?.method || entry?.method || '').toUpperCase(),
    path: message?.path || entry?.path || entry?.url || '',
    brandId: message?.brandId || entry?.brandId || null,
    locationId: message?.locationId || entry?.locationId || null,
    storeId: message?.storeId || entry?.storeId || null,
    orderId: message?.orderId || entry?.orderId || null,
    system: message?.system || entry?.system || null,
    requestTimestamp:
      message?.requestTimestamp || entry?.requestTimestamp || entry?.['@timestamp'] || '',
    status: Number(message?.status ?? entry?.status ?? 0),
    responseTime: message?.responseTime || entry?.responseTime || '',
    success: Boolean(
      message?.success ?? message?.response?.success ?? entry?.success ?? false
    ),
    responseMessage: extractedResponseMsg,
    resMessage: message?.resMessage || entry?.resMessage || '',
    response: message?.response || entry?.response || null,
    ip: extractedIp,
    requestSource: message?.requestSource || entry?.requestSource || {},
    userAgent: message?.userAgent || entry?.userAgent || null,
    userId: message?.userId || entry?.userId || entry?.adminUserId || null,
    username: extractedUsername,
    userName: extractedUsername,
    module: message?.module || entry?.module || '',
    action: extractedAction,
    apiAction: extractedAction,
    mainBrandId: message?.mainBrandId || entry?.mainBrandId || null,
    ref_id: message?.ref_id || entry?.ref_id || entry?.refId || null,
    requestQuery: extractedQuery,
    query: extractedQuery,
    requestBody: extractedBody,
    body: extractedBody,
  };

  return {
    '@timestamp':
      entry?.['@timestamp'] || entry?.timestamp || mergedMessage.requestTimestamp || new Date().toISOString(),
    '@ptr':
      entry?.['@ptr'] ||
      entry?.ptr ||
      entry?._id ||
      `${mergedMessage.logId}_${mergedMessage.requestTimestamp || Date.now()}`,
    '@message': mergedMessage,
  };
};

export const getLogUniqueKey = (entry: CloudWatchLogEntry, index: number): string => {
  if (entry['@ptr']) return entry['@ptr'];
  const msg = entry['@message'];
  if (msg?.logId) return msg.logId;
  return `${entry['@timestamp']}_${msg?.method}_${msg?.path}_${index}`;
};

export const mergeUniqueLogs = (
  existingLogs: CloudWatchLogEntry[],
  newLogs: CloudWatchLogEntry[]
): CloudWatchLogEntry[] => {
  const seen = new Set<string>();
  const result: CloudWatchLogEntry[] = [];

  for (let i = 0; i < existingLogs.length; i++) {
    const key = getLogUniqueKey(existingLogs[i], i);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(existingLogs[i]);
    }
  }

  for (let i = 0; i < newLogs.length; i++) {
    const key = getLogUniqueKey(newLogs[i], result.length);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(newLogs[i]);
    }
  }

  return result;
};

// ── Initial State ─────────────────────────────────────────────────────────────

const createInitialTabState = (defaultModule: string): TabLogData => ({
  logs: [],
  total: 0,
  page: 1,
  limit: 20,
  nextCursor: null,
  hasMore: false,
  loading: false,
  isFetchingMore: false,
  module: defaultModule,
  moduleLabel: '',
});

const initialState: ActivityLogState = {
  activeTab: 'admin',
  modulesLoading: false,
  modules: [],
  admin: createInitialTabState('apiRequests'),
  backend: createInitialTabState('backendRequests'),
  loading: false,
  isFetchingMore: false,
  logs: [],
  total: 0,
  page: 1,
  limit: 20,
  nextCursor: null,
  hasMore: false,
  module: 'apiRequests',
  moduleLabel: '',
  message: '',
  success: false,
};

// ── Slice ─────────────────────────────────────────────────────────────────────

const activitylogSlice = createSlice({
  name: 'activitylog',
  initialState,
  reducers: {
    // Switch active tab
    setActiveTab(state, action: PayloadAction<ActivityLogTabType>) {
      const tab = action.payload;
      state.activeTab = tab;
      const tabData = state[tab];
      state.logs = tabData.logs;
      state.total = tabData.total;
      state.page = tabData.page;
      state.limit = tabData.limit;
      state.nextCursor = tabData.nextCursor;
      state.hasMore = tabData.hasMore;
      state.loading = tabData.loading;
      state.isFetchingMore = tabData.isFetchingMore;
      state.module = tabData.module;
      state.moduleLabel = tabData.moduleLabel;
    },

    // Fetch module list
    getModules(state) {
      state.modulesLoading = true;
    },
    getModulesSuccess(
      state,
      action: PayloadAction<GetModulesSuccessPayload | ActivityLogState['modules']>
    ) {
      state.modulesLoading = false;
      const payload = action.payload;
      const rawData =
        payload && !Array.isArray(payload) && 'data' in payload && payload.data !== undefined
          ? payload.data
          : payload;
      state.modules = Array.isArray(rawData) ? rawData : [];
    },
    getModulesFailed(state, action: PayloadAction<GetModulesFailedPayload>) {
      state.modulesLoading = false;
      state.message = action.payload?.message || '';
    },

    // Fetch logs — payload forwarded to POST /activityHistory/getLogs
    getActivityLogs(state, action: PayloadAction<GetLogsPayload>) {
      const isAppend = Boolean(
        action.payload?.nextCursor ||
        (action.payload?.page && action.payload.page > 1)
      );
      const targetTab: ActivityLogTabType =
        action.payload?.tab ||
        (action.payload?.module === 'apiRequests' ? 'admin' : 'backend');

      state.activeTab = targetTab;
      state.message = '';

      if (isAppend) {
        state[targetTab].isFetchingMore = true;
        state.isFetchingMore = true;
      } else {
        // Clear that tab's logs when fetching fresh
        state[targetTab].loading = true;
        state[targetTab].logs = [];
        state[targetTab].nextCursor = null;
        state[targetTab].hasMore = false;
        state[targetTab].total = 0;
        state[targetTab].page = 1;

        state.loading = true;
        state.logs = [];
        state.nextCursor = null;
        state.hasMore = false;
        state.total = 0;
        state.page = 1;
      }
    },
    getActivityLogsSuccess(
      state,
      action: PayloadAction<GetActivityLogsSuccessPayload | Record<string, any>>
    ) {
      const payload = action.payload;
      const res =
        payload && 'res' in payload && payload.res !== undefined
          ? payload.res
          : payload;
      const targetTab: ActivityLogTabType =
        (payload && 'tab' in payload && payload.tab) ||
        (res?.module === 'apiRequests' ? 'admin' : 'backend');
      const isAppend = Boolean(payload && 'isAppend' in payload && payload.isAppend);
      const requestedLimit =
        (payload && 'requestedLimit' in payload && payload.requestedLimit) || 20;

      state[targetTab].loading = false;
      state[targetTab].isFetchingMore = false;

      const rawLogs = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.logs)
        ? res.data.logs
        : Array.isArray(res?.logs)
        ? res.logs
        : [];

      const normalizedLogs = rawLogs.map(normalizeLogEntry);
      const returnedCursor =
        res?.nextCursor ||
        res?.data?.nextCursor ||
        res?.cursor ||
        res?.data?.cursor ||
        res?.nextToken ||
        res?.data?.nextToken ||
        res?.pagination?.nextCursor ||
        null;

      const cleanCursor =
        returnedCursor && returnedCursor !== 'null' && returnedCursor !== 'undefined'
          ? String(returnedCursor)
          : null;

      state[targetTab].nextCursor = cleanCursor;

      const combinedLogs = isAppend
        ? mergeUniqueLogs(state[targetTab].logs, normalizedLogs)
        : normalizedLogs;

      state[targetTab].logs = combinedLogs;
      state[targetTab].page = isAppend
        ? state[targetTab].page + 1
        : res?.page || res?.data?.page || 1;

      const rawTotal =
        typeof res?.total === 'number'
          ? res.total
          : typeof res?.data?.total === 'number'
          ? res.data.total
          : null;

      state[targetTab].total =
        rawTotal && rawTotal > combinedLogs.length
          ? rawTotal
          : combinedLogs.length;

      // Determine hasMore
      if (res?.hasMore !== undefined) {
        state[targetTab].hasMore = Boolean(res.hasMore);
      } else if (res?.data?.hasMore !== undefined) {
        state[targetTab].hasMore = Boolean(res.data.hasMore);
      } else if (cleanCursor) {
        state[targetTab].hasMore = normalizedLogs.length > 0;
      } else if (rawTotal !== null) {
        state[targetTab].hasMore = combinedLogs.length < rawTotal;
      } else {
        state[targetTab].hasMore = normalizedLogs.length >= requestedLimit;
      }

      state[targetTab].limit = res?.limit || requestedLimit;
      if (res?.module) state[targetTab].module = res.module;
      if (res?.moduleLabel) state[targetTab].moduleLabel = res.moduleLabel;

      // Sync active tab mirrors if this response belongs to currently active tab
      if (state.activeTab === targetTab) {
        state.loading = false;
        state.isFetchingMore = false;
        state.success = res?.success ?? true;
        state.logs = state[targetTab].logs;
        state.total = state[targetTab].total;
        state.page = state[targetTab].page;
        state.limit = state[targetTab].limit;
        state.nextCursor = state[targetTab].nextCursor;
        state.hasMore = state[targetTab].hasMore;
        state.module = state[targetTab].module;
        state.moduleLabel = state[targetTab].moduleLabel;
      }
    },
    getActivityLogsFailed(
      state,
      action: PayloadAction<GetActivityLogsFailedPayload>
    ) {
      const targetTab: ActivityLogTabType = action.payload?.tab || state.activeTab;
      state[targetTab].loading = false;
      state[targetTab].isFetchingMore = false;

      if (state.activeTab === targetTab) {
        state.loading = false;
        state.isFetchingMore = false;
        state.success = false;
        state.message = action.payload?.message || 'Failed to fetch logs';
      }
    },

    clearData(state) {
      state.activeTab = 'admin';
      state.modulesLoading = false;
      state.loading = false;
      state.isFetchingMore = false;
      state.message = '';
      state.success = false;
      state.logs = [];
      state.total = 0;
      state.page = 1;
      state.nextCursor = null;
      state.hasMore = false;
      state.admin = createInitialTabState('apiRequests');
      state.backend = createInitialTabState('backendRequests');
    },
  },
});

export const activitylogAction = activitylogSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectActiveTab = (state: RootState) => state.activitylog.activeTab;
export const selectAdminLogData = (state: RootState) => state.activitylog.admin;
export const selectBackendLogData = (state: RootState) => state.activitylog.backend;
export const selectActivityModules = (state: RootState) => state.activitylog.modules;
export const selectActivityModulesLoading = (state: RootState) => state.activitylog.modulesLoading;

// Dynamic active-tab selectors
export const selectActivityLogs = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.logs ?? state.activitylog.logs;
export const selectActivityLogTotal = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.total ?? state.activitylog.total;
export const selectActivityLogPage = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.page ?? state.activitylog.page;
export const selectActivityLogLimit = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.limit ?? state.activitylog.limit;
export const selectActivityLogNextCursor = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.nextCursor ?? state.activitylog.nextCursor;
export const selectActivityLogHasMore = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.hasMore ?? state.activitylog.hasMore;
export const selectActivityLogIsFetchingMore = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.isFetchingMore ?? state.activitylog.isFetchingMore;
export const selectActivityModule = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.module ?? state.activitylog.module;
export const selectActivityModuleLabel = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.moduleLabel ?? state.activitylog.moduleLabel;
export const selectIsLoading = (state: RootState) =>
  state.activitylog[state.activitylog.activeTab]?.loading ?? state.activitylog.loading;
export const selectIsSuccess = (state: RootState) => state.activitylog.success;
export const selectMessage = (state: RootState) => state.activitylog.message;

// Legacy selector kept for backward compat (returns whole state.activitylog)
export const getActivityLogsData = (state: RootState) => state.activitylog;

const activitylogReducer = activitylogSlice.reducer;
export default activitylogReducer;

